import { preview } from '@/components/form/preview'
import { useResidentModalsApi } from '@/components/modals/ResidentModals'
import { ContentCopy, Sync } from '@mui/icons-material'
import { Alert, IconButton, Link, Stack, Tooltip } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { Header } from './components/Header'
import { useAccountData } from './hooks/useAccountData'
import { useActions } from './hooks/useActions'
import { useTexts } from './hooks/useTexts'
import { useResetApiKeyModal } from './modals/ResetApiKeyModal'
import { useApiKey } from '../hooks/useApiKey'

type Props = {
  onEdit: () => void
}

export function RmAccountView(props: Props) {
  const { onEdit } = props

  const texts = useTexts()
  const { apiKey, apiKeyEnabled } = useApiKey()
  const { accountData, gotAccessToApiKey } = useAccountData()
  const { onCopyApiKey } = useActions()

  const { Modal: ResetKeyModal, show: showResetKeyModal } = useResetApiKeyModal()
  const {
    supportTicket: { show: showSupportTicketModal },
  } = useResidentModalsApi()

  return (
    <preview.Root
      applyPendingFitMapOnMount
      header={<Header onEdit={onEdit} />}
      testId="account-settings-view"
    >
      <preview.Field label={texts.name}>{accountData.name}</preview.Field>
      <preview.Field label={texts.surname}>{accountData.surname}</preview.Field>
      <preview.Field label={texts.email}>{accountData.email}</preview.Field>
      <preview.Field label={texts.username}>{accountData.username}</preview.Field>

      {gotAccessToApiKey && (
        <>
          <preview.Field label={texts.apiKey}>
            {apiKeyEnabled ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                {apiKey}
                <Tooltip title={texts.copy}>
                  <IconButton
                    onClick={onCopyApiKey}
                    size="small"
                    data-trackid="copy-api-key-button"
                    data-testid="copy-api-key-button"
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
                <Tooltip title={texts.reset}>
                  <IconButton
                    onClick={showResetKeyModal}
                    size="small"
                    data-trackid="reset-api-key-button"
                    data-testid="reset-api-key-button"
                  >
                    <Sync />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              texts.apiKeyNotSet
            )}
          </preview.Field>

          {!apiKeyEnabled && (
            <Alert severity="info">
              <FormattedMessage
                id="settings.rmAccount.view.requestAPIKey"
                values={{
                  a: chunks => (
                    <Link
                      href="#"
                      onClick={() =>
                        showSupportTicketModal({
                          topic: 'API',
                          successMessage: texts.requestApiKeySuccess,
                        })
                      }
                    >
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Alert>
          )}
        </>
      )}

      <ResetKeyModal />
    </preview.Root>
  )
}
